/**
 * Per-world looks for the backdrop planet, keyed by codex id (`aubade-backdrop`). A profile is plain
 * data; `PlanetStage` builds only the layers it names. Verdigris is today's planet exactly; Aubade
 * is a world losing its air to the solar wind; any other pinned place gets a generic Earth-like
 * world seeded from its id, so no two places share a face.
 *
 * Vectors are in the stage's view space: the camera looks down −z, +x is right, +y is up.
 */
import { CLOUD_BAKE_FRAGMENT, PLANET_FRAGMENT, SURFACE_BAKE_FRAGMENT } from "./planetShaders";
import { FADING_CLOUD_BAKE_FRAGMENT, FADING_PLANET_FRAGMENT, FADING_SURFACE_BAKE_FRAGMENT } from "./fadingShaders";
import { DEFAULT_PLANET_PLACEMENT, type PlanetPlacement } from "./backdropLayout";

export type Vec3 = readonly [number, number, number];

/** The wind layer (`WindLayer.ts`): the stripped limb, the plume and tail, and the streaks. */
export type WindProfile = {
  /** How far the tail curves toward screen-up as it runs downwind (per radius², in radii). */
  bend: number;
  /** The tail's falloff length, in planet radii. */
  length: number;
  /** How fast gas runs down the tail, radii per second of the shared clock. */
  flowSpeed: number;
  /** Brightness of the exosphere at the limb, and of the plume. */
  limb: number;
  tail: number;
  limbColor: Vec3;
  /** Lit gas near the planet, shading toward `tailColor` downwind (which also tints gas in shadow). */
  coreColor: Vec3;
  tailColor: Vec3;
  streaks: { count: number; color: Vec3 };
  /** The gas pass's target size, as a fraction of the viewport's CSS pixels. */
  resolution: number;
};

export type PlanetProfile = {
  /** Which look this is: `NarrativeBackdrop` sets it as `data-planet`, which picks the CSS theme. */
  key: "verdigris" | "aubade" | "generic";
  /** Offsets the bake noise: one world, one seed. */
  seed: Vec3;
  /** The key light's direction. A wind blows directly away from it. */
  light: Vec3;
  placement: PlanetPlacement;
  axialTilt: number;
  /** The bake shaders and the frame shader that lights them. */
  shaders: { surface: string; clouds: string; planet: string };
  /** THE FADING, 0..1 (the fading family's `uBleach`; Verdigris's shaders ignore it). */
  bleach: number;
  /** Cloud drift, texture widths per second of the shared clock. */
  cloudDrift: number;
  atmosphere: Vec3;
  /** The back-faced Fresnel shell's scale; absent on Aubade, whose limb is the wind's. */
  shell?: number;
  /** Bake and draw the night-lights map (the fading family only). */
  nightLights: boolean;
  traffic: { count: number; color: Vec3 };
  wind?: WindProfile;
};

export const VERDIGRIS: PlanetProfile = {
  key: "verdigris",
  seed: [3.1, -7.4, 11.2],
  light: [-0.85, 0.32, 0.36],
  placement: DEFAULT_PLANET_PLACEMENT,
  axialTilt: 0.38,
  shaders: { surface: SURFACE_BAKE_FRAGMENT, clouds: CLOUD_BAKE_FRAGMENT, planet: PLANET_FRAGMENT },
  bleach: 0,
  cloudDrift: 0.0012,
  atmosphere: [0.42, 0.86, 0.74],
  shell: 1.075,
  nightLights: false,
  traffic: { count: 180, color: [0.85, 1.0, 0.92] },
};

export const AUBADE: PlanetProfile = {
  key: "aubade",
  seed: [-5.3, 2.2, 8.7],
  // The star is low, left and in front: a lit gibbous with a night crescent on the right, and the
  // tail streaming off up and away to the right.
  light: [-0.78, -0.12, 0.55],
  // Smaller and further left than Verdigris, so the tail has room to cross the open stage.
  placement: { scale: 0.6, x: 0.28 },
  axialTilt: 0.22,
  shaders: { surface: FADING_SURFACE_BAKE_FRAGMENT, clouds: FADING_CLOUD_BAKE_FRAGMENT, planet: FADING_PLANET_FRAGMENT },
  bleach: 1,
  cloudDrift: 0.002,
  atmosphere: [0.5, 0.68, 1.0],
  nightLights: true,
  // Evacuation carriers.
  traffic: { count: 120, color: [1.0, 0.9, 0.78] },
  wind: {
    bend: 0.022,
    length: 6.5,
    flowSpeed: 0.55,
    limb: 2.4,
    tail: 0.9,
    limbColor: [0.62, 0.8, 1.0],
    coreColor: [0.45, 0.62, 1.0],
    tailColor: [0.36, 0.34, 0.95],
    streaks: { count: 140, color: [0.7, 0.82, 1.0] },
    resolution: 0.4,
  },
};

const GENERIC: Omit<PlanetProfile, "seed"> = {
  key: "generic",
  light: VERDIGRIS.light,
  placement: DEFAULT_PLANET_PLACEMENT,
  axialTilt: 0.38,
  shaders: AUBADE.shaders,
  bleach: 0,
  cloudDrift: 0.0012,
  atmosphere: [0.48, 0.7, 1.0],
  shell: 1.075,
  nightLights: false,
  traffic: { count: 180, color: [0.9, 0.95, 1.0] },
};

const PROFILES: Record<string, PlanetProfile> = { verdigris: VERDIGRIS, aubade: AUBADE };

/** The look for a pinned codex entry: its own profile, or the generic world seeded from its id. */
export function planetProfile(codexId: string): PlanetProfile {
  return PROFILES[codexId] ?? { ...GENERIC, seed: seedFromId(codexId) };
}

/** A stable noise offset from an id: FNV-1a, split into three coordinates in [-10, 10). */
export function seedFromId(id: string): Vec3 {
  let h = 0x811c9dc5;
  for (let i = 0; i < id.length; i++) h = Math.imul(h ^ id.charCodeAt(i), 0x01000193);
  const coord = (shift: number) => (((h >>> shift) & 0x3ff) / 0x400) * 20 - 10;
  return [coord(0), coord(10), coord(20)];
}
